/**
 * 工作区HTTP API服务
 *
 * 封装工作区相关的REST API调用，用于工作区CRUD操作。
 * 支持 Cookie 认证（推荐）和 Authorization Header 认证。
 */

import type { Workspace, WorkspaceCreate, WorkspaceUpdate } from '../types/workspace';

// API基础URL配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// HTTP错误类
export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ConflictError extends HttpError {
  constructor(message: string = '资源冲突') {
    super(message, 409, 'CONFLICT');
    this.name = 'ConflictError';
  }
}

export class QuotaExceededError extends HttpError {
  constructor(message: string = '已达配额上限') {
    super(message, 403, 'QUOTA_EXCEEDED');
    this.name = 'QuotaExceededError';
  }
}

export class ValidationError extends HttpError {
  constructor(message: string = '数据验证失败') {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

// 响应类型定义
interface WorkspaceListResponse {
  workspaces: Workspace[];
  total: number;
}

/**
 * 获取请求头
 * 
 * @param token - 可选的认证令牌，如果提供则使用 Header 认证，否则使用 Cookie 认证
 */
function getHeaders(token?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * 处理HTTP响应错误
 */
async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // 无法解析JSON，使用默认错误消息
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      // 检查是否是配额超限
      if (errorMessage.includes('配额') || errorMessage.includes('上限')) {
        throw new QuotaExceededError(errorMessage);
      }
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    case 409:
      throw new ConflictError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

/**
 * 工作区API服务类
 */
export class WorkspaceApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 获取工作区列表
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @returns 工作区列表
   */
  async listWorkspaces(token?: string): Promise<Workspace[]> {
    const response = await fetch(`${this.baseUrl}/api/workspaces`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data: WorkspaceListResponse = await response.json();
    return data.workspaces;
  }

  /**
   * 创建工作区
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param data - 工作区创建数据
   * @returns 创建的工作区
   */
  async createWorkspace(token: string | undefined, data: WorkspaceCreate): Promise<Workspace> {
    const response = await fetch(`${this.baseUrl}/api/workspaces`, {
      method: 'POST',
      headers: getHeaders(token),
      credentials: 'include', // 携带 Cookie
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取工作区详情
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   * @returns 工作区详情
   */
  async getWorkspace(token: string | undefined, workspaceId: number): Promise<Workspace> {
    const response = await fetch(`${this.baseUrl}/api/workspaces/${workspaceId}`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

/**
   * 智能生成工作区封面（异步模式）
   *
   * 提交封面生成任务后立即返回，后台继续轮询完成生成。
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区 ID
   * @param description - 用户补充描述（可选）
   * @returns 任务提交状态和提示消息
   */
  async generateCover(
    token: string | undefined,
    workspaceId: number,
    description?: string
  ): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/workspaces/${workspaceId}/cover/generate`, {
      method: 'POST',
      headers: getHeaders(token),
      credentials: 'include',
      body: JSON.stringify({ description }),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data = await response.json();
    return { status: data.status, message: data.message };
  }

  /**
   * 删除工作区
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   */
  async deleteWorkspace(token: string | undefined, workspaceId: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/workspaces/${workspaceId}`, {
      method: 'DELETE',
      headers: getHeaders(token),
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  /**
   * 上传工作区封面
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   * @param file - 图片文件
   * @returns 封面路径
   */
  async uploadCover(
    token: string | undefined,
    workspaceId: number,
    file: File
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseUrl}/api/workspaces/${workspaceId}/cover`, {
      method: 'POST',
      headers: {
        // 不设置 Content-Type，让浏览器自动设置 multipart/form-data
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include', // 携带 Cookie
      body: formData,
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data = await response.json();
    return data.cover_path;
  }

  /**
   * 获取工作区 Token 统计
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区 ID
   * @returns Token 总消耗量
   */
  async getTokenStats(token: string | undefined, workspaceId: number): Promise<number> {
    const response = await fetch(`${this.baseUrl}/api/workspaces/${workspaceId}/token-stats`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    const data = await response.json();
    return data.total_tokens;
  }

  /**
   * 获取用户每日 Token 配额
   *
   * @returns 用户当日 Token 使用量和限额
   */
  async getUserTokenQuota(): Promise<{
    used_tokens: number;
    daily_limit: number;
    percentage: number;
  }> {
    const response = await fetch(`${this.baseUrl}/api/workspaces/user/token-quota`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取工作区按天的 Token 统计
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区 ID
   * @param days - 统计天数（默认7天）
   * @returns 按天的 Token 使用量统计
   */
  async getDailyTokenStats(
    token: string | undefined,
    workspaceId: number,
    days: number = 7
  ): Promise<{
    workspace_id: number;
    days: number;
    total_tokens: number;
    daily_stats: Array<{
      date: string;
      tokens: number;
      request_count: number;
    }>;
  }> {
    const response = await fetch(
      `${this.baseUrl}/api/workspaces/${workspaceId}/token-stats/daily?days=${days}`,
      {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include', // 携带 Cookie
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }
}

// 导出单例实例
export const workspaceApi = new WorkspaceApiService();

export default workspaceApi;
